import { LanguageSwitch } from 'modules/layout/components/LanguageSwitch.tsx';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LogoutButton } from 'shared/components/LogoutButton.tsx';
import { AuthContext } from 'shared/contexts/AuthContext.tsx';
import { useApi } from 'shared/hooks/useApi.ts';
import { useSitemap } from 'shared/hooks/useSitemap.ts';
import { ConnectisLogo, ConnectisTabletLogo, CrossIcon, HamburgerIcon } from 'shared/icons';
import { mergeClasses } from 'shared/utils/mergeClasses.ts';
import { MeetingType } from 'types/Meeting/MeetingType.ts';
import { mapDtoToMeetingType } from 'types/Meeting/mapper.ts';
import { useRealtimer } from '../../../shared/hooks/useRealtimer.ts';
import { Menu } from './Menu.tsx';
import { UpcomingMeeting } from './UpcomingMeeting.tsx';

export const Navbar = () => {
  const navigate = useNavigate();
  const sitemap = useSitemap();
  const { pathname } = useLocation();
  const { dashboardId } = useParams();

  const { isLogoutAllowed } = useContext(AuthContext);

  const [menuOpen, setMenuOpen] = useState(false);

  const { data: upcomingMeetingData, refetch } = useApi<MeetingType>(`upcoming-meeting`, {
    mapFn: mapDtoToMeetingType,
  });

  useEffect(() => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  }, [pathname]);

  useRealtimer(`ClientDashboardMeetingUpdated-${dashboardId}`, () => {
    refetch();
  });

  return (
    <div className="fixed left-0 top-0 z-50 w-screen overflow-hidden bg-white bg-gradient-c bg-center px-4 py-4 sm:left-auto sm:top-6 sm:h-[calc(100dvh-48px)] sm:max-w-[85px] sm:rounded-xl sm:py-6 xl:max-w-[216px]">
      <div className="absolute left-0 top-0 mx-auto block h-screen w-screen bg-ellipse-shadow-mobile bg-left-top sm:bg-ellipse-shadow" />
      <div className="relative z-10 flex h-full flex-col">
        <div className="flex justify-between">
          <button
            className="block w-[120px] sm:mx-auto sm:w-[30px] xl:w-[137px]"
            role="link"
            onClick={() => navigate(sitemap.dashboard)}
          >
            <ConnectisLogo className="block sm:hidden xl:block" />
            <ConnectisTabletLogo className="hidden sm:block xl:hidden" />
          </button>
          <button
            className="block text-white sm:hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <CrossIcon className="h-6 w-6" /> : <HamburgerIcon className="h-6 w-6" />}
          </button>
        </div>
        <div
          className={mergeClasses(
            'flex h-[calc(100dvh-56px)] flex-col items-stretch justify-between overflow-hidden transition-all sm:h-full sm:max-h-[100dvh] sm:items-center xl:items-stretch',
            menuOpen ? 'max-h-[calc(100dvh-56px)]' : 'max-h-0',
          )}
        >
          <Menu />
          <div>
            <div className="mb-12 space-y-4 sm:hidden">
              {isLogoutAllowed && <LogoutButton />}
              <LanguageSwitch />
            </div>
            {upcomingMeetingData && (
              <UpcomingMeeting
                title={upcomingMeetingData.title}
                url={upcomingMeetingData.url}
                displayDate={upcomingMeetingData.displayDate}
                displayTime={upcomingMeetingData.displayTime}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
